/**
 * ReplayControls - barre de contrôles de lecture (partagée vue simple / double).
 */

import type { ReplayPlayback } from '../hooks/useReplayPlayback';
import { Switch } from './Switch';

interface ReplayControlsProps {
  playback: ReplayPlayback;
  /** Contrôles additionnels insérés avant le slider (ex. permutation des tables). */
  extra?: React.ReactNode;
}

export default function ReplayControls({ playback, extra }: ReplayControlsProps) {
  const {
    steps, stepIndex, isPlaying, speed, setSpeed,
    harmonizedDelays, setHarmonizedDelays,
    atStart, atEnd,
    nextSubStep, prevSubStep, handlePrevTrick, handleNextTrick,
    handleRangeChange, toggleAutoPlay, stopAutoPlay,
  } = playback;

  return (
    <div className="replay-controls">
      <button
        className="replay-btn"
        onClick={handlePrevTrick}
        disabled={atStart}
        title="Pli precedent (fleche haut)"
      >
        ⏮
      </button>

      <button
        className="replay-btn"
        onClick={() => { stopAutoPlay(); prevSubStep(); }}
        disabled={atStart}
        title="Carte precedente (fleche gauche)"
      >
        ◀
      </button>

      <button
        className={`replay-btn replay-btn-play-pause ${isPlaying ? 'active' : ''}`}
        onClick={toggleAutoPlay}
        title="Lecture/Pause (P)"
      >
        {isPlaying ? '⏸' : '▶'}
      </button>

      <button
        className="replay-btn"
        onClick={() => { stopAutoPlay(); nextSubStep(); }}
        disabled={atEnd}
        title="Carte suivante (fleche droite / espace)"
      >
        ▶
      </button>

      <button
        className="replay-btn"
        onClick={handleNextTrick}
        disabled={stepIndex >= steps.length - 1}
        title="Pli suivant (fleche bas)"
      >
        ⏭
      </button>

      <div className="replay-controls-divider" />

      <select
        className="replay-speed-select"
        value={speed}
        onChange={(e) => setSpeed(Number(e.target.value))}
        title="Vitesse de lecture"
      >
        <option value={0.5}>0.5x</option>
        <option value={1}>1x</option>
        <option value={2}>2x</option>
        <option value={4}>4x</option>
      </select>

      <label className="replay-toggle-label" title="Utiliser un delai uniforme entre chaque carte">
        Délais harmonisés
        <Switch
          compact
          checked={harmonizedDelays}
          onChange={setHarmonizedDelays}
        />
      </label>

      {extra && (
        <>
          <div className="replay-controls-divider" />
          {extra}
        </>
      )}

      <div className="replay-controls-divider" />

      <input
        type="range"
        className="replay-range-slider"
        min={0}
        max={steps.length - 1}
        value={stepIndex}
        onChange={(e) => handleRangeChange(Number(e.target.value))}
        title={`Etape ${stepIndex + 1} / ${steps.length}`}
      />
    </div>
  );
}
